import { useEffect, useRef } from 'react';
import lottie from 'lottie-web';

/* ------------------------------------------------------------------
   lottie-web glue for the two prototype animations (the hub's prize
   reveal and the story's chest reveal). The animation data arrives
   through `load()` — a dynamic import, so the JSON and its images stay
   out of the main bundle — and the hook hands back a controller whose
   commands can be issued at any time: before the animation is ready
   they're remembered and the last one is applied once it is.
   ------------------------------------------------------------------ */

/* animation data loaders, memoised so a chunk is fetched once */
const chunks = {};
const once = (key, importer) => () => {
  if (!chunks[key]) chunks[key] = importer().then((m) => m.default);
  return chunks[key];
};
export const loadHubPrizes = once('hubPrizes', () => import('../assets/lottie/hubPrizes'));
export const loadChestReveal = once('chestReveal', () => import('../assets/lottie/chestReveal'));

/* The svg renderer doesn't wait for its images (they'd pop in mid-animation),
   so load them up front. Resolves when they're all in, or after `maxMs`. */
function preloadImages(data, maxMs) {
  const urls = (data.assets || []).filter((a) => a.p && !a.layers).map((a) => a.p);
  if (!urls.length) return Promise.resolve();
  return new Promise((resolve) => {
    let left = urls.length;
    const done = () => { left -= 1; if (left === 0) resolve(); };
    urls.forEach((u) => { const img = new Image(); img.onload = done; img.onerror = done; img.src = u; });
    if (maxMs) setTimeout(resolve, maxMs);
  });
}

/* Fetch the chunk and warm the browser cache with its images, so the
   animation can start the moment it's mounted. Fire-and-forget. */
export function prefetchLottie(load) {
  load().then((data) => preloadImages(data)).catch(() => {});
}

function makeController() {
  let anim = null;
  let pending = null; // the last command issued before the animation was ready
  const run = (fn) => { if (anim) fn(anim); else pending = fn; };
  return {
    play: () => run((a) => a.play()),
    pause: () => run((a) => a.pause()),
    goToAndPlay: (frame) => run((a) => a.goToAndPlay(frame, true)),
    goToAndStop: (frame) => run((a) => a.goToAndStop(frame, true)),
    attach(a) { anim = a; if (pending) { const fn = pending; pending = null; fn(a); } },
    detach() { anim = null; pending = null; },
  };
}

/* how long a mount waits for the images before showing the animation anyway */
const IMAGE_WAIT_MS = 2500;

/* Builds the animation into `holderRef` (svg renderer, never autoplays)
   and returns its controller. `load`/`loop`/`preserveAspectRatio` are
   fixed for the life of the holder. */
export function useLottie(holderRef, { load, loop = false, preserveAspectRatio } = {}) {
  const ctlRef = useRef(null);
  if (!ctlRef.current) ctlRef.current = makeController();

  useEffect(() => {
    const ctl = ctlRef.current;
    let anim = null;
    let gone = false;
    load()
      .then((data) => preloadImages(data, IMAGE_WAIT_MS).then(() => data))
      .then((data) => {
        if (gone || !holderRef.current) return;
        anim = lottie.loadAnimation({
          container: holderRef.current,
          renderer: 'svg',
          loop,
          autoplay: false,
          animationData: data,
          rendererSettings: preserveAspectRatio ? { preserveAspectRatio } : undefined,
        });
        anim.addEventListener('DOMLoaded', () => { if (!gone) ctl.attach(anim); });
      })
      .catch((e) => console.log('GOLDEN QUEST LOTTIE FAILED', e));
    return () => {
      gone = true;
      ctl.detach();
      if (anim) anim.destroy();
    };
  }, [holderRef, load, loop, preserveAspectRatio]);

  return ctlRef.current;
}
